import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

/**
 * Patches every currently-cached `appointmentsCalendar` result that contains the given
 * appointment id, applying `patch(draft)` to that row. Returns an array of patch
 * results that can be `.undo()`'d on mutation failure.
 *
 * We iterate every active subscription (the user may have multiple calendar queries
 * open — main grid + sidebar own-day fetch) so the change shows up everywhere.
 */
function patchCalendarRow(api, apptId, patchRow) {
  const state = api.getState();
  const subs = appointmentsApi.util.selectInvalidatedBy(state, [{ type: 'Appointment', id: 'LIST' }]);
  const patches = [];
  for (const { endpointName, originalArgs } of subs) {
    if (endpointName !== 'appointmentsCalendar') continue;
    patches.push(
      api.dispatch(
        appointmentsApi.util.updateQueryData('appointmentsCalendar', originalArgs, (draft) => {
          if (!Array.isArray(draft)) return;
          const row = draft.find((a) => a.id === apptId);
          if (row) patchRow(row);
        }),
      ),
    );
  }
  return patches;
}

export const appointmentsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listAppointments: build.query({
      query: (filter) => `/appointments${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((a) => ({ type: 'Appointment', id: a.id })),
              { type: 'Appointment', id: 'LIST' },
            ]
          : [{ type: 'Appointment', id: 'LIST' }],
    }),
    getAppointment: build.query({
      query: (id) => `/appointments/${id}`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'Appointment', id }],
    }),
    appointmentsCalendar: build.query({
      query: ({ from, to, assignedUserId }) => {
        const p = new URLSearchParams();
        if (from) p.append('from', from);
        if (to) p.append('to', to);
        if (assignedUserId) p.append('assignedUserId', assignedUserId);
        return `/appointments/calendar?${p.toString()}`;
      },
      transformResponse: (resp) => resp.data,
      // Per-row tags so a single appointment invalidation refetches the calendar
      // AND so optimistic updates target the right cache entries.
      providesTags: (result) =>
        Array.isArray(result)
          ? [
              ...result.map((a) => ({ type: 'Appointment', id: a.id })),
              { type: 'Appointment', id: 'LIST' },
            ]
          : [{ type: 'Appointment', id: 'LIST' }],
    }),
    createAppointment: build.mutation({
      query: (body) => ({ url: '/appointments', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Appointment', id: 'LIST' }, 'Dashboard'],
      // Optimistic insert — slot the new appointment into any calendar query whose
      // window covers its scheduledAt so the card appears immediately.
      async onQueryStarted(body, { dispatch, getState, queryFulfilled }) {
        const tempId = `__optimistic-${Date.now()}`;
        const optimisticRow = {
          id: tempId,
          patientId: body.patientId ?? null,
          leadId: body.leadId ?? null,
          assignedUserId: body.assignedUserId,
          scheduledAt: body.scheduledAt,
          durationMinutes: body.durationMinutes ?? 30,
          status: 'Scheduled',
          type: body.type ?? 'Consultation',
          notes: body.notes ?? null,
          patientName: null,
          assignedUserName: null,
          assignedUserColorTag: null,
          createdAt: new Date().toISOString(),
        };
        const apptTs = new Date(body.scheduledAt).getTime();
        const state = getState();
        const subs = appointmentsApi.util.selectInvalidatedBy(state, [{ type: 'Appointment', id: 'LIST' }]);
        const patches = [];
        for (const { endpointName, originalArgs } of subs) {
          if (endpointName !== 'appointmentsCalendar') continue;
          const fromTs = originalArgs?.from ? new Date(originalArgs.from).getTime() : -Infinity;
          const toTs   = originalArgs?.to   ? new Date(originalArgs.to).getTime()   :  Infinity;
          if (apptTs < fromTs || apptTs >= toTs) continue;
          patches.push(
            dispatch(
              appointmentsApi.util.updateQueryData('appointmentsCalendar', originalArgs, (draft) => {
                if (Array.isArray(draft)) draft.push(optimisticRow);
              }),
            ),
          );
        }
        try { await queryFulfilled; } catch { patches.forEach((p) => p.undo()); }
        // The invalidatesTags above will trigger a refetch on success, replacing the
        // optimistic row with the real one — no need to remove it manually.
      },
    }),
    updateAppointmentStatus: build.mutation({
      query: ({ id, status, reason, sessionPackageId }) => ({ url: `/appointments/${id}/status`, method: 'PATCH', body: { status, reason, sessionPackageId } }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Appointment', id }, { type: 'Appointment', id: 'LIST' }],
      async onQueryStarted({ id, status }, api) {
        const patches = patchCalendarRow(api, id, (row) => { row.status = status; });
        try { await api.queryFulfilled; } catch { patches.forEach((p) => p.undo()); }
      },
    }),
    // Extends an appointment's duration in-place via PUT /appointments/:id.
    extendAppointment: build.mutation({
      query: ({ id, assignedUserId, scheduledAt, durationMinutes, notes }) => ({
        url: `/appointments/${id}`,
        method: 'PUT',
        body: { assignedUserId, scheduledAt, durationMinutes, notes },
      }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Appointment', id }, { type: 'Appointment', id: 'LIST' }],
      async onQueryStarted({ id, durationMinutes, notes }, api) {
        const patches = patchCalendarRow(api, id, (row) => {
          row.durationMinutes = durationMinutes;
          if (notes !== undefined) row.notes = notes;
        });
        try { await api.queryFulfilled; } catch { patches.forEach((p) => p.undo()); }
      },
    }),
    // Reassigns the appointment to a different doctor in place (same slot), with audit trail.
    reassignAppointment: build.mutation({
      query: ({ id, newAssignedUserId, reason }) => ({
        url: `/appointments/${id}/reassign`,
        method: 'PATCH',
        body: { newAssignedUserId, reason },
      }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Appointment', id }, { type: 'Appointment', id: 'LIST' }],
      async onQueryStarted({ id, newAssignedUserId }, api) {
        const patches = patchCalendarRow(api, id, (row) => { row.assignedUserId = newAssignedUserId; });
        try { await api.queryFulfilled; } catch { patches.forEach((p) => p.undo()); }
      },
    }),
    // Reschedules — backend creates a NEW appointment and marks the original as Rescheduled.
    rescheduleAppointment: build.mutation({
      query: ({ id, newScheduledAt, newDurationMinutes, reason }) => ({
        url: `/appointments/${id}/reschedule`,
        method: 'PATCH',
        body: { newScheduledAt, newDurationMinutes, reason },
      }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Appointment', id }, { type: 'Appointment', id: 'LIST' }],
    }),
    // Sets/clears which billed procedure an appointment is for. Also how a historical appointment
    // (which shows the generic "Session") gets attributed — those are deliberately not backfilled.
    // Invalidates the session ledger too, since changing this moves a per-procedure balance.
    updateAppointmentProcedure: build.mutation({
      query: ({ id, treatmentTypeId }) => ({
        url: `/appointments/${id}/procedure`,
        method: 'PATCH',
        body: { treatmentTypeId: treatmentTypeId || null },
      }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id, patientId }) => [
        { type: 'Appointment', id }, { type: 'Appointment', id: 'LIST' },
        ...(patientId ? [{ type: 'SessionLedger', id: patientId }, { type: 'Patient', id: patientId }] : []),
      ],
      async onQueryStarted({ id, treatmentTypeId, treatmentTypeName }, api) {
        const patches = patchCalendarRow(api, id, (row) => {
          row.treatmentTypeId = treatmentTypeId || null;
          row.treatmentTypeName = treatmentTypeName || null;
        });
        try { await api.queryFulfilled; } catch { patches.forEach((p) => p.undo()); }
      },
    }),
    // Admin-only: one-shot data fix for patients whose 2nd+ appointment was mistakenly typed Consultation.
    reclassifyConsultations: build.mutation({
      query: () => ({ url: '/appointments/reclassify-consultations', method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Appointment', id: 'LIST' }],
    }),
  }),
});

export const {
  useListAppointmentsQuery, useLazyListAppointmentsQuery,
  useAppointmentsCalendarQuery, useLazyAppointmentsCalendarQuery,
  useGetAppointmentQuery,
  useCreateAppointmentMutation, useUpdateAppointmentStatusMutation,
  useExtendAppointmentMutation, useRescheduleAppointmentMutation,
  useReassignAppointmentMutation, useReclassifyConsultationsMutation,
  useUpdateAppointmentProcedureMutation,
} = appointmentsApi;
